<template>
  <b-nav-item-dropdown v-if="hasMultiple" right no-caret class="asset-class-bar" @show="closeOpenPanels">
    <!-- Closed trigger: an orange pill with a white rounded square showing the asset-class 2-letter
    code (coloured per class) + a white caret. Compact and responsive — no long name. -->
    <template slot="button-content">
      <span v-b-tooltip.hover.bottom :title="$t('navbar.assetClass.selectorTitle')" class="ac-pill">
        <span class="ac-code-badge" :class="codeClass(selected)">{{ codeFor(selected) }}</span>
        <i class="fas fa-chevron-down small ac-trigger-caret"></i>
      </span>
    </template>
    <b-dropdown-header class="asset-class-header">{{ $t('navbar.assetClass.selectorTitle') }}</b-dropdown-header>
    <b-dropdown-item
      v-for="slug in availableAssetClasses"
      :key="slug"
      class="asset-class-option"
      href="#"
      :class="slug === selected ? 'currentAssetClass' : ''"
      @click.prevent="select(slug)"
    >
      <b-row no-gutters align-v="center" class="asset-class-row">
        <b-col cols="auto" class="asset-class-col-indicator">
          <span class="ac-badge" :class="badgeClass(slug)">{{ codeFor(slug) }}</span>
        </b-col>
        <b-col class="asset-class-col-content">
          <div class="asset-class-name">{{ nameFor(slug) }}</div>
          <div class="asset-class-desc">{{ descFor(slug) }}</div>
          <div v-if="countersFor(slug)" class="asset-class-counters">
            <span class="counter-number">{{ countersFor(slug).activeProducts }}</span>
            <span class="counter-label">{{ $t('navbar.assetClass.productsActiveLabel') }}</span>
            <span class="counter-sep">-</span>
            <span class="counter-number">{{ countersFor(slug).totalIssuers }}</span>
            <span class="counter-label">{{ $t('navbar.assetClass.issuersLabel') }}</span>
          </div>
        </b-col>
      </b-row>
    </b-dropdown-item>
  </b-nav-item-dropdown>
</template>

<script>
import { switchAssetClass } from '../../../utils/assetClassSwitch'
import { confirmDiscardUnsavedChanges } from '../../../utils/unsavedChanges'

// CSS classes (not inline styles, per CSP) — one modifier class per known asset class slug. Panel
// rows use a filled badge (coloured background); the closed trigger uses a white badge with coloured
// text (see .ac-code-badge--* below).
const ASSET_CLASS_BADGE_CLASSES = {
  ibips: 'ac-badge--ibips',
  'investment-certificates': 'ac-badge--investment-certificates',
}
const ASSET_CLASS_CODE_CLASSES = {
  ibips: 'ac-code-badge--ibips',
  'investment-certificates': 'ac-code-badge--investment-certificates',
}

// Two-letter code shown in the badge (trigger + option rows). Falls back to the first two letters of
// the slug for any future asset class not listed here.
const ASSET_CLASS_CODES = {
  ibips: 'IB',
  'investment-certificates': 'IC',
}

export default {
  name: 'AssetClassBar',

  computed: {
    availableAssetClasses() {
      return this.$store.getters.getAvailableAssetClasses
    },
    selected() {
      return this.$store.getters.getSelectedAssetClass
    },
    hasMultiple() {
      return this.$store.getters.hasMultipleAssetClasses
    },
    currentName() {
      return this.selected ? this.nameFor(this.selected) : ''
    },
  },

  methods: {
    nameFor(slug) {
      return this.$t(`navbar.assetClass.${slug}.name`)
    },
    descFor(slug) {
      return this.$t(`navbar.assetClass.${slug}.description`)
    },
    codeFor(slug) {
      return ASSET_CLASS_CODES[slug] || (slug ? slug.slice(0, 2).toUpperCase() : '')
    },
    badgeClass(slug) {
      return ASSET_CLASS_BADGE_CLASSES[slug] || 'ac-badge--default'
    },
    codeClass(slug) {
      return ASSET_CLASS_CODE_CLASSES[slug] || 'ac-code-badge--default'
    },
    countersFor(slug) {
      const counters = this.$store.getters.getAssetClassCounters
      return (counters && counters[slug]) || null
    },
    select(slug) {
      return confirmDiscardUnsavedChanges(this).then((proceed) => {
        if (proceed) {
          switchAssetClass({ store: this.$store, router: this.$router }, slug)
        }
      })
    },
    closeOpenPanels() {
      this.$emit('on-close-open-panels')
    },
  },
}
</script>

<style lang="scss">
.asset-class-bar {
  // b-nav-item-dropdown forwards the class we pass to the outer <li>, not to the inner
  // .nav-link.dropdown-toggle. The pill provides the visual, so the toggle itself is padding-only.
  .nav-link.dropdown-toggle {
    display: inline-flex;
    align-items: center;
    padding: 2px 4px;
  }

  .dropdown-menu {
    padding: 16px 0;
    min-width: 320px;
    border-radius: 10px;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
  }
}

// Closed-trigger pill: translucent white background, white code badge, white caret.
.ac-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  padding: 4px 10px 4px 5px;
}

.ac-trigger-caret {
  color: #fff;
}

.ac-code-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 6px;
  background-color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.ac-code-badge--ibips {
  color: #2563eb;
}

.ac-code-badge--investment-certificates {
  color: #f97316;
}

.ac-code-badge--default {
  color: #6b7280;
}

// Filled 2-letter code badge used in the panel option rows (coloured background, white text).
.ac-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #fff;
}

.ac-badge--ibips {
  background-color: #2563eb;
}

.ac-badge--investment-certificates {
  background-color: #f97316;
}

.ac-badge--default {
  background-color: #9ca3af;
}

// 2-column grid: indicator (badge) | content. (The selection is shown by the row highlight below, so
// there is no separate check column any more.) align-items:center keeps the badge vertically centred
// against the whole name/desc/counters block instead of drifting to the top of the cell.
.asset-class-row {
  width: 100%;
  align-items: center;
}

.asset-class-col-indicator {
  width: 28px;
  min-width: 28px;
  max-width: 28px;
  margin-right: 16px !important;
  color: #111827;
  display: flex;
  align-items: center;
  justify-content: center;
}

.asset-class-icon {
  width: 20px !important;
  height: 20px !important;
}

.asset-class-col-content {
  min-width: 0;
  margin-right: 0px !important;
}

// b-dropdown-header forwards the class we pass to the outer <li>, not to the inner .dropdown-header
// element — so this must be a descendant selector to reach the element with the default padding.
.asset-class-header .dropdown-header {
  font-size: 0.72rem !important;
  font-weight: 600;
  text-transform: uppercase;
  color: #6b7280 !important;
  padding: 0 20px 12px !important;
}

.asset-class-option a {
  display: block !important;
  padding: 12px 20px !important;
  color: black !important;
}

.asset-class-option a:hover {
  background-color: #f8f9fa;
}

.asset-class-option .asset-class-name {
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 6px;
  margin-right: 0px !important;
}

.asset-class-option .asset-class-desc {
  font-size: 0.85rem;
  color: #6b7280;
  white-space: normal;
  margin-bottom: 8px;
  margin-right: 0px !important;
}

// Counters: flex + gap so the numbers never stick to their labels (vue-loader condenses the template
// whitespace between the spans, which is why plain inline spans rendered as "302active products").
.asset-class-option .asset-class-counters {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.05rem 0.35rem;
  font-size: 0.82rem;
}

.asset-class-option .asset-class-counters .counter-number {
  font-weight: 600;
  color: #4b5563;
}

.asset-class-option .asset-class-counters .counter-label {
  font-weight: 400;
  color: #6b7280;
}

.asset-class-option .asset-class-counters .counter-sep {
  color: #9ca3af;
}

// Selected row: subtle highlight as the selection cue.
.asset-class-option.currentAssetClass a {
  background-color: #fceae5;
}

.currentAssetClass .asset-class-name {
  font-weight: 700 !important;
  margin-right: 0px !important;
}
</style>
